import React, { useEffect, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  PartyPopper,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';

const COLLAPSED_STORAGE_KEY = 'asm_setup_checklist_collapsed';
const DISMISSED_STORAGE_KEY = 'asm_setup_checklist_dismissed';

function ChecklistItem({ complete, label, href }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-800 bg-slate-950/65 px-4 py-3">
      <div className="flex items-start gap-3">
        <span className={complete ? 'text-emerald-400' : 'text-slate-500'}>
          {complete ? <CheckCircle2 className="mt-0.5 h-5 w-5" /> : <Circle className="mt-0.5 h-5 w-5" />}
        </span>
        <div>
          <p className={complete ? 'font-medium text-slate-200' : 'font-medium text-slate-300'}>
            {label}
          </p>
          {!complete && href ? (
            <Link className="mt-1 inline-flex text-sm text-blue-400 hover:text-blue-300" to={href}>
              Set up
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function QuickSetupChecklist() {
  const [open, setOpen] = useState(() => localStorage.getItem(COLLAPSED_STORAGE_KEY) !== 'true');
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true');
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    users: 1,
    devices: 0,
    policies: 0,
    channels: 0,
  });

  useEffect(() => {
    let active = true;

    const loadChecklist = async () => {
      const results = await Promise.allSettled([
        api.get('/users', { params: { page: 1, page_size: 1 } }),
        api.get('/devices', { params: { page: 1, page_size: 1 } }),
        api.get('/policies', { params: { page: 1, page_size: 1 } }),
        api.get('/notifications/channels', { params: { page: 1, page_size: 1 } }),
      ]);

      if (!active) return;

      setCounts({
        users: results[0].status === 'fulfilled' ? results[0].value.data?.total || 1 : 1,
        devices: results[1].status === 'fulfilled' ? results[1].value.data?.total || 0 : 0,
        policies: results[2].status === 'fulfilled' ? results[2].value.data?.total || 0 : 0,
        channels: results[3].status === 'fulfilled' ? results[3].value.data?.total || 0 : 0,
      });
      setLoading(false);
    };

    loadChecklist();

    return () => {
      active = false;
    };
  }, []);

  const items = [
    { id: 'org', label: 'Create your organization', complete: true, href: null },
    { id: 'users', label: 'Invite team members', complete: counts.users > 1, href: '/users' },
    { id: 'devices', label: 'Enroll a device', complete: counts.devices > 0, href: '/enrollment' },
    {
      id: 'policies',
      label: 'Configure a monitoring policy',
      complete: counts.policies > 0,
      href: '/policies',
    },
    {
      id: 'notifications',
      label: 'Set up alert notifications',
      complete: counts.channels > 0,
      href: '/alerts',
    },
  ];

  const completedCount = items.filter((item) => item.complete).length;
  const allComplete = completedCount === items.length;

  useEffect(() => {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, open ? 'false' : 'true');
  }, [open]);

  if (!loading && allComplete && dismissed) {
    return null;
  }

  return (
    <Card className="border-slate-700 bg-slate-800/95 shadow-[0_24px_60px_rgba(2,6,23,0.35)]">
      <Collapsible onOpenChange={setOpen} open={open}>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-slate-500">Quick Setup</p>
              <CardTitle className="mt-2 text-white">
                {allComplete ? 'ASM deployment complete' : 'Finish your workspace setup'}
              </CardTitle>
              <p className="mt-2 text-sm text-slate-400">
                {allComplete
                  ? 'All set! Your organization is fully configured.'
                  : `${completedCount} of ${items.length} complete`}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {allComplete ? (
                <Button
                  className="bg-emerald-500 text-slate-950 hover:bg-emerald-400"
                  onClick={() => {
                    localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
                    setDismissed(true);
                  }}
                  type="button"
                >
                  Dismiss forever
                </Button>
              ) : null}

              <CollapsibleTrigger asChild>
                <Button
                  className="border border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                  type="button"
                >
                  {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {open ? 'Minimize' : 'Expand'}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>

          <Progress
            className="mt-4 h-2 bg-slate-900 [&>div]:bg-gradient-to-r [&>div]:from-blue-500 [&>div]:to-emerald-400"
            value={(completedCount / items.length) * 100}
          />
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="space-y-3 pt-0">
            {allComplete ? (
              <div className="rounded-3xl border border-emerald-500/25 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.18),_transparent_55%),rgba(2,6,23,0.92)] p-6">
                <div className="flex items-center gap-3">
                  <span className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-emerald-300">
                    <PartyPopper className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-semibold text-white">All set! Your organization is fully configured.</p>
                    <p className="mt-1 text-sm text-slate-400">
                      Monitoring, policy coverage, and notifications are ready for rollout.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              items.map((item) => (
                <ChecklistItem
                  key={item.id}
                  complete={item.complete}
                  href={item.href}
                  label={item.label}
                />
              ))
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
